/**
 * Simple HTML sanitizer to prevent XSS attacks
 * 
 * This allows only a limited subset of HTML tags and attributes
 * and properly preserves formatting tags (b, strong, i, em, u)
 * @param {string} html The HTML string to sanitize
 * @returns {string} Sanitized HTML
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';
  
  // Create a new div element to use the browser's HTML parser
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Normalize line breaks: Ensure plain text line breaks become proper HTML
  const normalizeLineBreaks = (node) => {
    if (node.nodeType === 3) { // Text node
      // Check if this text node has line breaks and is not inside a pre tag
      if (node.textContent.includes('\n') && !isInsidePre(node)) {
        const fragments = document.createDocumentFragment();
        const textParts = node.textContent.split('\n');
        
        textParts.forEach((text, i) => {
          // Add the text
          fragments.appendChild(document.createTextNode(text));
          
          // Add a <br> after each part except the last one
          if (i < textParts.length - 1) {
            fragments.appendChild(document.createElement('br'));
          }
        });
        
        // Replace the original text node with our new fragments
        node.parentNode.replaceChild(fragments, node);
      }
    }
  };
  
  // Check if a node is inside a pre tag
  const isInsidePre = (node) => {
    let parent = node.parentNode;
    while (parent) {
      if (parent.nodeName.toLowerCase() === 'pre') {
        return true;
      }
      parent = parent.parentNode;
    }
    return false;
  };

  // Fix the issue with empty divs that are created by Enter key presses
  const fixEmptyDivs = (container) => {
    // Convert divs to paragraphs (often created by Enter key)
    const divs = container.querySelectorAll('div');
    divs.forEach(div => {
      // Create a new paragraph
      const p = document.createElement('p');
      
      // Copy all children of the div to the paragraph
      while (div.firstChild) {
        p.appendChild(div.firstChild);
      }
      
      // If the paragraph is empty or just contains whitespace, add a <br> to maintain the line break
      if (!p.textContent.trim()) {
        p.appendChild(document.createElement('br'));
      }
      
      // Replace the div with the paragraph
      div.parentNode.replaceChild(p, div);
    });
    
    // Fix empty paragraphs
    const emptyParagraphs = container.querySelectorAll('p:empty');
    emptyParagraphs.forEach(p => {
      p.appendChild(document.createElement('br'));
    });
  };
  
  // Function to sanitize a single node
  const sanitizeNode = (node) => {
    // Only process element nodes
    if (node.nodeType !== 1) return;
    
    const tagName = node.nodeName.toLowerCase();
    
    // Allow only specific tags with their formatting preserved
    // Note: 'span' is included for Quill's formatting system
    const allowedTags = ['p', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'br', 'span'];
    
    // Limited set of attributes that are safe
    const allowedAttrs = ['class', 'style'];
    
    // For disallowed tags, preserve their content
    if (!allowedTags.includes(tagName)) {
      // Create a document fragment to hold the node's children
      const fragment = document.createDocumentFragment();
      
      // Move each child to the fragment (preserving their content)
      while (node.firstChild) {
        fragment.appendChild(node.firstChild);
      }
      
      // Replace the node with its content
      node.parentNode.replaceChild(fragment, node);
      return;
    }
    
    // For spans, only allow those with formatting-related classes
    if (tagName === 'span') {
      const classes = node.getAttribute('class') || '';
      // Check if this is a Quill formatting span
      if (!classes.includes('ql-') && 
          !classes.includes('ql-format') && 
          !node.style.fontWeight && 
          !node.style.fontStyle && 
          !node.style.textDecoration) {
        
        // Not a formatting span, convert to its content
        const fragment = document.createDocumentFragment();
        while (node.firstChild) {
          fragment.appendChild(node.firstChild);
        }
        node.parentNode.replaceChild(fragment, node);
        return;
      }
    }
    
    // Remove all attributes except those in allowedAttrs
    const attributes = Array.from(node.attributes);
    for (const attr of attributes) {
      const attrName = attr.name;
      if (!allowedAttrs.includes(attrName)) {
        node.removeAttribute(attrName);
      }
    }
    
    // Special handling for style attribute - only allow safe properties
    if (node.hasAttribute('style')) {
      const safeStyles = [
        'font-weight', 
        'font-style', 
        'text-decoration',
        'text-align'
      ];
      
      const style = node.getAttribute('style');
      const styleProps = style.split(';').filter(Boolean);
      const safeStyleProps = styleProps.filter(prop => {
        const propName = prop.split(':')[0].trim();
        return safeStyles.some(safeProp => propName === safeProp);
      });
      
      if (safeStyleProps.length > 0) {
        node.setAttribute('style', safeStyleProps.join(';'));
      } else {
        node.removeAttribute('style');
      }
    }
    
    // Handle empty paragraphs (preserve them for line breaks)
    if (tagName === 'p' && (!node.textContent.trim() && !node.querySelector('br'))) {
      const br = document.createElement('br');
      node.appendChild(br);
    }
    
    // Recursively sanitize child nodes
    const childNodes = Array.from(node.childNodes);
    for (const child of childNodes) {
      sanitizeNode(child);
    }
  };
  
  // Fix DIVs that are created by Enter key presses
  fixEmptyDivs(tempDiv);
  
  // First, handle any top-level text nodes with line breaks
  Array.from(tempDiv.childNodes).forEach(node => {
    if (node.nodeType === 3) {
      normalizeLineBreaks(node);
    }
  });
  
  // Now sanitize all nodes
  Array.from(tempDiv.childNodes).forEach(node => {
    if (node.nodeType === 1) { // Element node
      sanitizeNode(node);
    }
  });
  
  // Ensure text content is wrapped in paragraphs
  // This is important for Quill's content structure
  const content = tempDiv.innerHTML;
  
  // If the sanitized content is empty, return an empty string
  if (!content.trim()) {
    return '';
  }
  
  return content;
};

export default sanitizeHtml; 